// FT-1 — feature registry integrity gate. The single source of truth for
// "is tooling/features/features.yaml well-formed?". Validated on every
// APK release (scripts/release.sh) and every web deploy
// (scripts/deploy-web.sh). Mirrors catalogValidation.ts's shape.
//
// Deliberately SHALLOW: checks the registry is structurally sound (valid
// shape, unique ids, valid status/tier enums) — it does NOT try to detect
// a shipped feature with no matching entry. That would need a heuristic
// against the codebase, and every other gate in this project is
// deterministic (typecheck/jest/security); a flaky completeness check
// would be the odd one out. Completeness stays a human call at ship
// time, not a build gate.

import { FeatureRegistry } from "./schemas/featureRegistry.js";

export interface FeatureFinding {
  id: string;
  rule: string;
  severity: "error";
  message: string;
}

export interface FeatureValidationResult {
  totalAreas: number;
  totalFeatures: number;
  errors: FeatureFinding[];
  ok: boolean;
}

/**
 * Validate a parsed features.yaml document. Pure: no IO, no YAML parsing
 * (the caller reads + parses the file; this only checks the shape).
 */
export function validateFeatures(data: unknown): FeatureValidationResult {
  const errors: FeatureFinding[] = [];

  const parsed = FeatureRegistry.safeParse(data);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      errors.push({
        id: issue.path.join(".") || "<root>",
        rule: "schema",
        severity: "error",
        message: issue.message,
      });
    }
    return { totalAreas: 0, totalFeatures: 0, errors, ok: false };
  }

  const { areas } = parsed.data;
  const seenAreaIds = new Map<string, number>();
  const seenFeatureIds = new Map<string, number>();
  let totalFeatures = 0;

  for (const area of areas) {
    seenAreaIds.set(area.id, (seenAreaIds.get(area.id) ?? 0) + 1);
    for (const feature of area.features) {
      totalFeatures++;
      seenFeatureIds.set(feature.id, (seenFeatureIds.get(feature.id) ?? 0) + 1);
    }
  }

  for (const [id, count] of seenAreaIds) {
    if (count > 1) {
      errors.push({
        id,
        rule: "duplicate_area_id",
        severity: "error",
        message: `area id "${id}" appears ${count}×`,
      });
    }
  }
  for (const [id, count] of seenFeatureIds) {
    if (count > 1) {
      errors.push({
        id,
        rule: "duplicate_feature_id",
        severity: "error",
        message: `feature id "${id}" appears ${count}×`,
      });
    }
  }

  // FT-2 semantic rules. Kept deliberately few and unambiguous: a gate
  // that fires on a judgement call gets skipped, and this one has to
  // survive being run on every build. The shape rules (valid enums,
  // required surfaces/roles) are already caught by safeParse above.
  for (const area of areas) {
    for (const feature of area.features) {
      const seenRoles = new Set<string>();
      for (const role of feature.roles) {
        if (seenRoles.has(role)) {
          errors.push({
            id: feature.id,
            rule: "duplicate_role",
            severity: "error",
            message: `role "${role}" is listed twice`,
          });
        }
        seenRoles.add(role);
      }

      // FT-3 — a liftExclusion on a feature that owes web nothing is dead
      // weight: it silences a pool entry that was never in the pool, and
      // the day the surface regresses to `absent` it would silence it for
      // real, with a `why` written for a different situation. Same shape
      // as the rule below: a declaration cross-checked against `surfaces`,
      // because a declaration that contradicts the data is worse than no
      // declaration at all.
      const webRnw = feature.surfaces["web-rnw"];
      if (feature.liftExclusion && webRnw !== "absent" && webRnw !== "partial") {
        errors.push({
          id: feature.id,
          rule: "lift_exclusion_without_debt",
          severity: "error",
          message: `liftExclusion is set but web-rnw is "${webRnw}" — nothing to exclude from the pool`,
        });
      }

      // A shipped feature that is MISSING everywhere contradicts itself:
      // it claims to exist and nothing carries it.
      //
      // The distinction that makes this rule safe is absent vs n-a.
      // "absent" is a gap — it should be here and it is not. "n-a" is a
      // declaration — this surface is not where it lives. A feature that
      // is n-a on every surface is legitimately surface-less (a build-time
      // gate, a one-off migration script, a historical fact about the
      // data), and saying so is an answer, not an omission. So the rule
      // fires only when something is actually absent.
      if (feature.status === "shipped") {
        const values = Object.values(feature.surfaces);
        const live = values.some((s) => s === "shipped" || s === "partial");
        const missing = values.some((s) => s === "absent");
        if (!live && missing) {
          errors.push({
            id: feature.id,
            rule: "shipped_nowhere",
            severity: "error",
            message:
              'status is "shipped" but every surface is absent — nothing carries it',
          });
        }
      }
    }
  }

  return {
    totalAreas: areas.length,
    totalFeatures,
    errors,
    ok: errors.length === 0,
  };
}

/** Compact human-readable report for the CLI / test failure message. */
export function formatFeaturesReport(r: FeatureValidationResult): string {
  const lines: string[] = [];
  lines.push(
    `features: ${r.totalAreas} areas · ${r.totalFeatures} features · ${r.errors.length} errors`,
  );
  for (const e of r.errors) lines.push(`  ERROR  ${e.id} [${e.rule}] ${e.message}`);
  return lines.join("\n");
}
